import React from 'react';
import { CalculatorIcon, CalendarIcon, FuelIcon, GaugeIcon, GitCompareArrowsIcon, MessageSquareIcon, RepeatIcon, CogIcon, CheckIcon } from 'lucide-react';
import { Car } from '../types/car';
import { formatARS, formatKm } from '../utils/format';
import { CarGallery } from './CarGallery';
interface CarCardProps {
  car: Car;
  isCompared: boolean;
  compareDisabled: boolean;
  onFinance: (car: Car) => void;
  onCompare: (id: string) => void;
  onTradeIn: (car: Car) => void;
  onInquiry: (car: Car) => void;
}
const secondaryBtn = 'flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border text-xs font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon disabled:cursor-not-allowed disabled:opacity-40';
export function CarCard({
  car,
  isCompared,
  compareDisabled,
  onFinance,
  onCompare,
  onTradeIn,
  onInquiry
}: CarCardProps) {
  const name = `${car.brand} ${car.model} ${car.version}`;
  const specs = [{
    label: 'Año',
    value: String(car.year),
    icon: CalendarIcon
  }, {
    label: 'Kilometraje',
    value: formatKm(car.km),
    icon: GaugeIcon
  }, {
    label: 'Transmisión',
    value: car.transmission,
    icon: CogIcon
  }, {
    label: 'Combustible',
    value: car.fuel,
    icon: FuelIcon
  }];
  return <article className={`flex flex-col overflow-hidden rounded-3xl bg-panel transition-shadow duration-200 ${isCompared ? 'shadow-neon-strong' : 'shadow-neon'}`} aria-label={name} data-id="element-2" data-mp="26yd73h1rb4s8">
      <CarGallery car={car} badge={<span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold backdrop-blur ${car.condition === '0 km' ? 'bg-neon text-ink' : 'border border-white/15 bg-ink/70 text-white'}`} data-id="element-4" data-mp="1x2jll86280yb">
            {car.condition}
          </span>} data-id="element-3" data-mp="1x1qoz00tertw" />

      <div className="flex flex-1 flex-col p-6" data-id="element-5" data-mp="1eaqujjajwpt8">
        <p className="text-xs font-medium uppercase tracking-wider text-neon" data-id="element-6" data-mp="1e9fqrjh3vi24">
          {car.brand} · {car.bodyType}
        </p>
        <h3 className="mt-1.5 font-display text-2xl font-semibold leading-tight tracking-tight text-white" data-id="element-7" data-mp="qevwglaznbjf">
          {car.model} <span className="font-normal text-muted" data-id="element-8" data-mp="lf9ksgat63cq">{car.version}</span>
        </h3>

        <dl className="mt-4 grid grid-cols-2 gap-2" data-id="element-9" data-mp="ggy9s28s1msx">
          {specs.map(({
          label,
          value,
          icon: Icon
        }) => <div key={label} className="flex items-center gap-2 rounded-xl bg-raised px-3 py-2 text-xs text-white/85" data-id="element-10" data-mp="1e4zyio8j338b">
              <dt className="sr-only" data-id="element-11" data-mp="196f0mvgr0vn1">{label}</dt>
              <Icon className="h-3.5 w-3.5 shrink-0 text-neon" aria-hidden="true" data-id="element-12" data-mp="193j2p74wi6yl" />
              <dd className="truncate" data-id="element-13" data-mp="148dg063oco9g">{value}</dd>
            </div>)}
        </dl>

        <p className="mt-4 line-clamp-3 min-h-[3.75rem] text-sm leading-5 text-white/65" data-id="element-14" data-mp="z6xvqg9bmls4">{car.description}</p>

        <div className="mt-auto pt-5" data-id="element-15" data-mp="3ouezdefdhdks">
          <div className="rounded-2xl border border-neon/30 bg-ink p-4" data-id="element-16" data-mp="3jwwwo5ry7ul8">
            <p className="text-xs font-medium text-muted" data-id="element-17" data-mp="1zfe2v69gj61d">Precio contado</p>
            <p className="mt-0.5 font-display text-3xl font-semibold leading-tight tracking-tight tabular-nums text-neon" data-id="element-18" data-mp="1zdb1jq2utqhc">
              {formatARS(car.cashPrice)}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-line pt-3" data-id="element-19" data-mp="29bz7vrlh8h57">
              <div data-id="element-20" data-mp="2e5bslqn1c3so">
                <p className="text-xs text-muted" data-id="element-21" data-mp="2e5v4oqq07xys">Anticipo</p>
                <p className="text-sm font-semibold tabular-nums text-white" data-id="element-22" data-mp="2j44yoxzzssit">
                  {car.financing ? formatARS(car.downPayment) : 'No aplica'}
                </p>
              </div>
              <div data-id="element-23" data-mp="2s3jpxznzh74d">
                <p className="text-xs text-muted" data-id="element-24" data-mp="2s531jcyp2tzt">{car.financing ? `${car.installments} cuotas aprox. de` : 'Financiación'}</p>
                <p className="text-sm font-semibold tabular-nums text-white" data-id="element-25" data-mp="2x2d0bj7z4i0b">
                  {car.financing ? formatARS(car.installmentAmount) : 'Solo contado'}
                </p>
              </div>
            </div>
          </div>

          <button type="button" onClick={() => car.financing ? onFinance(car) : onInquiry(car)} className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-neon text-sm font-semibold text-ink shadow-neon-btn transition-colors duration-150 hover:bg-neon-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-neon focus-visible:ring-offset-2 focus-visible:ring-offset-panel" data-id="element-26" data-mp="2t5yxpt7z91le">
            {car.financing ? <CalculatorIcon className="h-4 w-4" aria-hidden="true" data-id="element-27" data-mp="25gmrn27y59nb" /> : <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-28" data-mp="25eti9f5y8bbm" />}
            {car.financing ? 'Simular financiación' : 'Consultar por esta unidad'}
          </button>

          <div className={`mt-2 grid gap-2 ${car.tradeIn ? 'grid-cols-3' : 'grid-cols-2'}`} data-id="element-29" data-mp="2ef86amaas4it">
            <button type="button" onClick={() => onCompare(car.id)} disabled={compareDisabled && !isCompared} aria-pressed={isCompared} title={compareDisabled && !isCompared ? 'Podés comparar hasta 3 vehículos' : undefined} className={`${secondaryBtn} ${isCompared ? 'border-neon bg-neon/15 text-neon' : 'border-line bg-raised text-white hover:border-neon/60 hover:text-neon'}`} data-id="element-30" data-mp="1qpnk7uiukr5b">
              {isCompared ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-31" data-mp="1ve0tzfect6jw" /> : <GitCompareArrowsIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-32" data-mp="20ahlmjkri31q" />}
              {isCompared ? 'Agregado' : 'Comparar'}
            </button>
            {car.tradeIn && <button type="button" onClick={() => onTradeIn(car)} className={`${secondaryBtn} border-line bg-raised text-white hover:border-neon/60 hover:text-neon`} data-id="element-33" data-mp="1qcbhrwfeamu8">
                <RepeatIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-34" data-mp="ewd7xqce7wry" />
                Permutar
              </button>}
            <button type="button" onClick={() => onInquiry(car)} className={`${secondaryBtn} border-line bg-raised text-white hover:border-neon/60 hover:text-neon`} data-id="element-35" data-mp="2clic7wqjav49">
              <MessageSquareIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-36" data-mp="22v79gdil6ryy" />
              Consultar
            </button>
          </div>
        </div>
      </div>
    </article>;
}